import { useCallback, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { QuoteDetailPanel } from "@/components/quotes/QuoteDetailPanel";
import { useDeleteQuote, useQuote } from "@/hooks/useQuotes";
import { invalidateEventViews, type CreatedEventQuote } from "@/hooks/useEventsPipeline";
import { isEmptyDraft } from "@/lib/event-pipeline";

interface EventQuotePanelProps {
  /** Quote to show in the side panel, or null. */
  quoteId: string | null;
  /** A draft "+ Quote" just made: the drawer opens on it in edit mode. */
  draft: CreatedEventQuote | null;
  onClose: () => void;
}

/**
 * Opens a quote from Events in place (#1429): the quote drawer (#1559), fed by
 * useQuote(id) because the drawer takes a full row.
 */
export function EventQuotePanel({ quoteId, draft, onClose }: EventQuotePanelProps) {
  const qc = useQueryClient();
  const deleteQuote = useDeleteQuote();
  const loadId = draft?.id ?? quoteId;
  const { data, isError } = useQuote(loadId);

  // A quote that won't load closes the panel, so the next chip still opens.
  useEffect(() => {
    if (isError && loadId) {
      toast.error("Couldn't load that quote");
      onClose();
    }
  }, [isError, loadId, onClose]);

  const quote = data?.id === loadId ? data : null;

  // Discard only removes a draft that is still empty: no total and no items.
  const { mutate: deleteQuoteMutate } = deleteQuote;
  const deleteIfStillEmpty = useCallback(
    async (d: { id: string; quote_number: string }) => {
      const { data: row, error } = await supabase
        .from("quotes")
        .select("id, status, total, event_id, line_items:quote_line_items(id)")
        .eq("id", d.id)
        .maybeSingle();
      if (error || !row) {
        toast.error("Couldn't check the draft, so it was kept");
        return;
      }
      if (!isEmptyDraft(row)) {
        toast.error(`${d.quote_number} has been edited, so it was kept`);
        return;
      }
      deleteQuoteMutate(d.id);
    },
    [deleteQuoteMutate],
  );

  const close = () => {
    const d = draft;
    onClose();
    invalidateEventViews(qc);
    if (!d) return;
    if (d.discardable) {
      toast(`Draft ${d.quote_number} is on ${d.event_number}`, {
        duration: 15_000,
        action: { label: "Discard draft", onClick: () => void deleteIfStillEmpty(d) },
      });
    } else {
      toast(
        `Draft ${d.quote_number} is ${d.event_number}'s only quote, so it can't be discarded here. Edit it into the real quote.`,
      );
    }
  };

  return (
    <QuoteDetailPanel
      quote={quote}
      open={!!quote}
      onOpenChange={(o) => {
        if (!o) close();
      }}
      // Deleted in the drawer: nothing left to offer "Discard draft" for.
      onDeleted={() => {
        onClose();
        invalidateEventViews(qc);
      }}
    />
  );
}
